import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { User, Lightbulb, Check, X, Mail, Trash2, BadgeCheck } from "lucide-react";
import { travelersApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import LoadingState from "@/components/shared/LoadingState";
import ErrorState from "@/components/shared/ErrorState";
import { cn } from "@/lib/utils";

interface Preference {
  kind: string;
  value: unknown;
  scope?: string;
}

interface Proposal {
  kind: string;
  value: unknown;
  count: number;
}

interface TravelerEmail {
  id: string;
  email: string;
  is_primary: boolean;
  verified?: boolean;
  matched_user_id?: string | null;
}

interface Traveler {
  id: string;
  display_name: string;
  age?: number | null;
  relationship?: string | null;
  warmth?: number | null;
  birthdate?: string | null;
  preferences?: Preference[];
  emails?: TravelerEmail[];
}

// ── helpers ──────────────────────────────────────────────────────────────────

function WarmthMeter({ value }: { value: number }) {
  const pct = Math.min(100, Math.max(0, value));
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>How well we know you</span>
        <span>{Math.round(pct)}%</span>
      </div>
      <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
        <div
          className="h-full rounded-full bg-primary transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

/** Find the current value for a preference kind, preferring scope=global. */
function prefValue(prefs: Preference[], kind: string): unknown {
  const matches = prefs.filter((p) => p.kind === kind);
  const global = matches.find((p) => p.scope === "global" || p.scope == null);
  return (global ?? matches[0])?.value ?? undefined;
}

// ── Chip button ───────────────────────────────────────────────────────────────

function Chip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
        active
          ? "border-foreground bg-foreground text-background"
          : "border-border bg-background text-muted-foreground hover:bg-muted",
      )}
    >
      {label}
    </button>
  );
}

// ── Range slider ──────────────────────────────────────────────────────────────

function PrefSlider({
  label,
  leftLabel,
  rightLabel,
  value,
  onChange,
  onCommit,
}: {
  label: string;
  leftLabel: string;
  rightLabel: string;
  value: number;
  onChange: (v: number) => void;
  onCommit: (v: number) => void;
}) {
  return (
    <div className="space-y-1.5">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground w-14 text-right shrink-0">{leftLabel}</span>
        <input
          type="range"
          min={0}
          max={10}
          step={1}
          value={value}
          aria-label={label}
          onChange={(e) => onChange(Number(e.target.value))}
          onPointerUp={(e) => onCommit(Number((e.target as HTMLInputElement).value))}
          onTouchEnd={(e) => onCommit(Number((e.target as HTMLInputElement).value))}
          className="w-full accent-primary"
        />
        <span className="text-xs text-muted-foreground w-14 shrink-0">{rightLabel}</span>
      </div>
    </div>
  );
}

// ── Identity edit form ────────────────────────────────────────────────────────

function IdentityEditor({ traveler }: { traveler: Traveler }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(traveler.display_name);
  const [relationship, setRelationship] = useState(traveler.relationship ?? "");
  // A <input type="date"> needs a bare YYYY-MM-DD; the API returns a full
  // timestamp (e.g. 1986-04-12T00:00:00.000Z), so trim to the date portion.
  const [birthdate, setBirthdate] = useState((traveler.birthdate ?? "").slice(0, 10));
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setName(traveler.display_name);
    setRelationship(traveler.relationship ?? "");
    setBirthdate((traveler.birthdate ?? "").slice(0, 10));
    setDirty(false);
  }, [traveler.id]);

  const mutation = useMutation({
    mutationFn: (body: { display_name?: string; birthdate?: string; relationship?: string }) =>
      travelersApi.update(traveler.id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["traveler", traveler.id] });
      setDirty(false);
    },
  });

  // Fix 3: guard against double-PATCH when blur fires before button click
  function handleSave() {
    if (mutation.isPending) return;
    mutation.mutate({
      display_name: name.trim() || traveler.display_name,
      relationship: relationship.trim() || undefined,
      birthdate: birthdate || undefined,
    });
  }

  return (
    <div className="rounded-lg border border-border bg-card p-4 space-y-3">
      <h2 className="text-sm font-semibold">Identity</h2>
      <div className="grid gap-2 sm:grid-cols-3">
        {/* Fix 4: associate labels with inputs via id/htmlFor */}
        <div className="space-y-1">
          <label htmlFor="identity-name" className="text-xs text-muted-foreground">Name</label>
          <input
            id="identity-name"
            type="text"
            value={name}
            onChange={(e) => { setName(e.target.value); setDirty(true); }}
            onBlur={handleSave}
            className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <div className="space-y-1">
          <label htmlFor="identity-relationship" className="text-xs text-muted-foreground">Relationship</label>
          <input
            id="identity-relationship"
            type="text"
            placeholder="e.g. wife, son"
            value={relationship}
            onChange={(e) => { setRelationship(e.target.value); setDirty(true); }}
            onBlur={handleSave}
            className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <div className="space-y-1">
          <label htmlFor="identity-birthdate" className="text-xs text-muted-foreground">Birthdate</label>
          <input
            id="identity-birthdate"
            type="date"
            value={birthdate}
            onChange={(e) => { setBirthdate(e.target.value); setDirty(true); }}
            onBlur={handleSave}
            className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
      </div>
      {dirty && (
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={handleSave} disabled={mutation.isPending}>
            {mutation.isPending ? "Saving…" : "Save changes"}
          </Button>
          {mutation.isError && (
            <p className="text-xs text-destructive">Failed to save.</p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Preference editor ─────────────────────────────────────────────────────────

function PreferenceEditor({ traveler }: { traveler: Traveler }) {
  const queryClient = useQueryClient();
  const prefs = traveler.preferences ?? [];

  // Fix 5: surface preference write errors
  const { mutate: setPref } = useMutation({
    mutationFn: (body: { kind: string; value: unknown }) =>
      travelersApi.setPreference(traveler.id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["traveler", traveler.id] });
    },
    onError: (e) => console.error("Failed to save preference", e),
  });

  // Read current values
  const seatVal = prefValue(prefs, "seat") as string | undefined;
  const bagVal = prefValue(prefs, "bag") as string | undefined;
  const adventureRaw = prefValue(prefs, "adventure_level") as { slider?: number } | undefined;
  const activityRaw = prefValue(prefs, "activity_level") as { slider?: number } | undefined;
  const airlineVal = prefValue(prefs, "airline") as string | undefined;
  const hotelVal = prefValue(prefs, "hotel") as string | undefined;

  // Fix 2: read booleans for interest chips so toggle-off works
  const foodOn = (prefValue(prefs, "food") as boolean | undefined) === true;
  const shoppingOn = (prefValue(prefs, "shopping") as boolean | undefined) === true;
  const historyOn = (prefValue(prefs, "history") as boolean | undefined) === true;

  // Fix 1: local slider state for instant visual feedback; commit only on release
  const [adventureSlider, setAdventureSlider] = useState(adventureRaw?.slider ?? 5);
  const [activitySlider, setActivitySlider] = useState(activityRaw?.slider ?? 5);

  // Sync slider + text inputs when server data refreshes
  useEffect(() => {
    const adv = (prefValue(prefs, "adventure_level") as { slider?: number } | undefined)?.slider ?? 5;
    const act = (prefValue(prefs, "activity_level") as { slider?: number } | undefined)?.slider ?? 5;
    setAdventureSlider(adv);
    setActivitySlider(act);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [traveler.preferences]);

  // Local text-input state (controlled while editing)
  const [airline, setAirline] = useState(airlineVal ?? "");
  const [hotel, setHotel] = useState(hotelVal ?? "");

  // Sync text inputs when server data refreshes
  useEffect(() => {
    setAirline((prefValue(prefs, "airline") as string | undefined) ?? "");
    setHotel((prefValue(prefs, "hotel") as string | undefined) ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [traveler.preferences]);

  return (
    <div className="rounded-lg border border-border bg-card p-4 space-y-5">
      <h2 className="text-sm font-semibold">What they like</h2>

      {/* Seat */}
      <div className="space-y-2">
        <p className="text-xs font-medium text-muted-foreground">Seat preference</p>
        <div className="flex gap-2">
          <Chip
            label="Window"
            active={seatVal === "window"}
            onClick={() => setPref({ kind: "seat", value: "window" })}
          />
          <Chip
            label="Aisle"
            active={seatVal === "aisle"}
            onClick={() => setPref({ kind: "seat", value: "aisle" })}
          />
        </div>
      </div>

      {/* Bag */}
      <div className="space-y-2">
        <p className="text-xs font-medium text-muted-foreground">Luggage</p>
        <div className="flex gap-2">
          <Chip
            label="Carry-on"
            active={bagVal === "carry_on"}
            onClick={() => setPref({ kind: "bag", value: "carry_on" })}
          />
          <Chip
            label="Checked"
            active={bagVal === "checked"}
            onClick={() => setPref({ kind: "bag", value: "checked" })}
          />
        </div>
      </div>

      {/* Fix 1: sliders use local state + commit on release */}
      <PrefSlider
        label="Adventure vs. Relax"
        leftLabel="Adventure"
        rightLabel="Relax"
        value={adventureSlider}
        onChange={setAdventureSlider}
        onCommit={(v) => setPref({ kind: "adventure_level", value: { slider: v } })}
      />
      <PrefSlider
        label="Activity pace"
        leftLabel="Chill"
        rightLabel="Packed"
        value={activitySlider}
        onChange={setActivitySlider}
        onCommit={(v) => setPref({ kind: "activity_level", value: { slider: v } })}
      />

      {/* Fix 2: interest chips toggle the current boolean */}
      <div className="space-y-2">
        <p className="text-xs font-medium text-muted-foreground">Interests</p>
        <div className="flex flex-wrap gap-2">
          <Chip
            label="Food"
            active={foodOn}
            onClick={() => setPref({ kind: "food", value: !foodOn })}
          />
          <Chip
            label="Shopping"
            active={shoppingOn}
            onClick={() => setPref({ kind: "shopping", value: !shoppingOn })}
          />
          <Chip
            label="History"
            active={historyOn}
            onClick={() => setPref({ kind: "history", value: !historyOn })}
          />
        </div>
      </div>

      {/* Airline */}
      <div className="space-y-1">
        <p className="text-xs font-medium text-muted-foreground">Preferred airline</p>
        <input
          type="text"
          placeholder="e.g. AA, UA, DL"
          value={airline}
          onChange={(e) => setAirline(e.target.value)}
          onBlur={() => {
            const v = airline.trim().toUpperCase();
            if (v) setPref({ kind: "airline", value: v });
          }}
          className="w-full max-w-xs rounded-md border border-input bg-background px-3 py-1.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </div>

      {/* Hotel */}
      <div className="space-y-1">
        <p className="text-xs font-medium text-muted-foreground">Preferred hotel brand</p>
        <input
          type="text"
          placeholder="e.g. Marriott, Hilton"
          value={hotel}
          onChange={(e) => setHotel(e.target.value)}
          onBlur={() => {
            const v = hotel.trim();
            if (v) setPref({ kind: "hotel", value: v });
          }}
          className="w-full max-w-xs rounded-md border border-input bg-background px-3 py-1.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </div>
    </div>
  );
}

// ── Proposal humaniser ────────────────────────────────────────────────────────

function humaniseKind(kind: string): string {
  switch (kind) {
    case "airline": return "preferred airline";
    case "seat": return "seat preference";
    case "bag": return "luggage";
    case "adventure_level":
    case "activity_level": return "travel style";
    case "food":
    case "shopping":
    case "history": return "interests";
    default: return kind;
  }
}

function renderValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "boolean") return value ? "yes" : "no";
  if (typeof value === "number") return String(value);
  return JSON.stringify(value);
}

// ── Suggestions card ─────────────────────────────────────────────────────────

function SuggestionsCard({ travelerId }: { travelerId: string }) {
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ["proposals", travelerId],
    queryFn: () => travelersApi.proposals(travelerId),
  });

  const proposals: Proposal[] = data?.proposals ?? [];

  const { mutate: confirm } = useMutation({
    mutationFn: (proposal: Proposal) =>
      travelersApi.confirmProposal(travelerId, { kind: proposal.kind, value: proposal.value }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["proposals", travelerId] });
      queryClient.invalidateQueries({ queryKey: ["traveler", travelerId] });
    },
  });

  const { mutate: dismiss } = useMutation({
    mutationFn: (proposal: Proposal) =>
      travelersApi.dismissProposal(travelerId, { kind: proposal.kind, value: proposal.value }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["proposals", travelerId] });
    },
  });

  if (proposals.length === 0) return null;

  return (
    <div className="rounded-lg border border-border bg-card p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Lightbulb className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        <h2 className="text-sm font-semibold">Suggestions</h2>
      </div>
      <ul className="space-y-2">
        {proposals.map((p) => {
          const displayValue = renderValue(p.value);
          const phrase = humaniseKind(p.kind);
          return (
            <li
              key={p.kind + ":" + displayValue}
              className="flex items-center justify-between gap-3 rounded-md bg-muted/40 px-3 py-2 text-sm"
            >
              <span className="flex-1 min-w-0">
                We noticed you keep choosing{" "}
                <span className="font-medium">{displayValue}</span>. Make it your {phrase}?
              </span>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  aria-label={`Confirm ${phrase}`}
                  onClick={() => confirm(p)}
                  className="rounded-md p-1.5 text-accent-foreground hover:bg-primary/10 transition-colors"
                >
                  <Check className="h-4 w-4" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  aria-label={`Dismiss ${phrase} suggestion`}
                  onClick={() => dismiss(p)}
                  className="rounded-md p-1.5 text-muted-foreground hover:bg-muted transition-colors"
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ── Emails ──────────────────────────────────────────────────────────────────

function EmailsSection({ traveler }: { traveler: Traveler }) {
  const queryClient = useQueryClient();
  const emails = traveler.emails ?? [];
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["traveler", traveler.id] });

  const addMut = useMutation({
    mutationFn: (email: string) => travelersApi.addEmail(traveler.id, { email, is_primary: emails.length === 0 }),
    onSuccess: () => { setValue(""); setError(null); invalidate(); },
    onError: (e: any) => setError(e?.response?.data?.error || "Couldn't add that email."),
  });
  const removeMut = useMutation({
    mutationFn: (emailId: string) => travelersApi.removeEmail(traveler.id, emailId),
    onSuccess: invalidate,
  });

  return (
    <div className="rounded-lg border border-border bg-card p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Mail className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        <h2 className="text-sm font-semibold">Email</h2>
      </div>
      <p className="text-xs text-muted-foreground">
        Used to send trip updates and to tell whether they already have a TripMuscle account (so a shared trip lands in theirs).
      </p>

      {emails.length > 0 && (
        <ul className="space-y-1.5">
          {emails.map((em) => (
            <li key={em.id} className="flex items-center gap-2 rounded-md bg-muted/40 px-3 py-1.5 text-sm">
              <span className="min-w-0 flex-1 truncate">{em.email}</span>
              {em.is_primary && <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">primary</span>}
              {em.matched_user_id ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
                  <BadgeCheck className="h-3 w-3" aria-hidden="true" /> TripMuscle user
                </span>
              ) : (
                <span className="text-[11px] text-muted-foreground">not a user yet</span>
              )}
              <button
                type="button"
                aria-label={"Remove " + em.email}
                onClick={() => removeMut.mutate(em.id)}
                className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <form
        className="flex items-center gap-2"
        onSubmit={(e) => { e.preventDefault(); const v = value.trim(); if (v) addMut.mutate(v); }}
      >
        <input
          type="email"
          placeholder="name@example.com"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="w-full max-w-xs rounded-md border border-input bg-background px-3 py-1.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />
        <Button size="sm" type="submit" disabled={addMut.isPending || !value.trim()}>
          {addMut.isPending ? "Adding…" : "Add email"}
        </Button>
      </form>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function TravelerProfile() {
  const { id } = useParams<{ id: string }>();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["traveler", id],
    queryFn: () => travelersApi.get(id!),
    enabled: !!id,
  });

  if (isLoading) return <LoadingState message="Loading traveler…" />;
  if (error) return <ErrorState message="Failed to load traveler" onRetry={() => refetch()} />;

  const traveler: Traveler | undefined = data?.traveler;
  if (!traveler) return <ErrorState message="Traveler not found" />;

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center shrink-0">
          <User className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{traveler.display_name}</h1>
          {traveler.relationship && (
            <p className="text-sm text-muted-foreground capitalize">{traveler.relationship}</p>
          )}
        </div>
      </div>

      {/* Warmth meter */}
      {traveler.warmth != null && <WarmthMeter value={traveler.warmth} />}

      {/* Identity editor */}
      <IdentityEditor traveler={traveler} />

      {/* Email(s) — for trip notifications + identifying a TripMuscle user */}
      <EmailsSection traveler={traveler} />

      {/* Suggestions card — shown above preferences when proposals exist */}
      <SuggestionsCard travelerId={traveler.id} />

      {/* Preference editor */}
      <PreferenceEditor traveler={traveler} />
    </div>
  );
}
